import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Label,
  ErrorText,
  InputGroup,
  IconWrapper,
  StyledInputMask,
  CalendarPopup,
  CalendarMonth,
  CalendarHeader,
  WeekDaysRow,
  DaysGrid,
  DayCell,
} from './components/SelectDateStyles';
import { useSelectDate } from './hooks/useSelectDate';
import { formatMonthTitle } from './utils/selectDateHelpers';

const SelectDate = ({
  name,
  label,
  value,
  onChange,
  disabled,
  error,
  size,
  isRange,
}) => {
  const {
    containerRef,
    isFocused,
    setIsFocused,
    isOpen,
    setIsOpen,
    currentDate,
    setCurrentDate,
    startDate,
    endDate,
    inputValue,
    handleInputChange,
    handleDayClick,
  } = useSelectDate({ name, value, onChange, disabled, isRange });

  const renderMonth = (monthMoment) => {
    const startOfMonth = monthMoment.clone().startOf('month');
    const startDayOfWeek = startOfMonth.day();
    const daysInMonth = monthMoment.daysInMonth();

    const blanks = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      blanks.push(<DayCell key={`blank-${i}`} isEmpty />);
    }

    const days = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const currentDayMoment = monthMoment.clone().date(d);

      let isSelected = false;
      let isRangeStart = false;
      let isRangeEnd = false;
      let isInRange = false;

      if (isRange) {
        if (startDate && currentDayMoment.isSame(startDate, 'day')) isRangeStart = true;
        if (endDate && currentDayMoment.isSame(endDate, 'day')) isRangeEnd = true;
        if (startDate && endDate && currentDayMoment.isBetween(startDate, endDate, 'day')) isInRange = true;
      } else {
        if (startDate && currentDayMoment.isSame(startDate, 'day')) isSelected = true;
      }

      days.push(
        <DayCell
          key={d}
          onClick={() => handleDayClick(currentDayMoment)}
          isSelected={isSelected}
          isRangeStart={isRangeStart}
          isRangeEnd={isRangeEnd}
          isInRange={isInRange}
        >
          {d}
        </DayCell>
      );
    }

    const totalSlots = [...blanks, ...days];

    return (
      <CalendarMonth>
        <CalendarHeader>
          <i
            className="fa fa-chevron-left"
            onClick={() => setCurrentDate(currentDate.clone().subtract(1, 'month'))}
          ></i>
          <span>{formatMonthTitle(monthMoment)}</span>
          <i
            className="fa fa-chevron-right"
            onClick={() => setCurrentDate(currentDate.clone().add(1, 'month'))}
          ></i>
        </CalendarHeader>
        <WeekDaysRow>
          <span>D</span><span>S</span><span>T</span><span>Q</span><span>Q</span><span>S</span><span>S</span>
        </WeekDaysRow>
        <DaysGrid>{totalSlots}</DaysGrid>
      </CalendarMonth>
    );
  };

  const maskStr = isRange ? '99/99/9999 - 99/99/9999' : '99/99/9999';

  return (
    <Container ref={containerRef}>
      {label && <Label disabled={disabled}>{label}</Label>}

      <InputGroup>
        <StyledInputMask
          name={name}
          mask={maskStr}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (!disabled) {
              setIsFocused(true);
              setIsOpen(true);
            }
          }}
          disabled={disabled}
          error={error}
          isFocused={isFocused}
          sizeVariant={size}
          placeholder={isRange ? 'DD/MM/AAAA - DD/MM/AAAA' : 'DD/MM/AAAA'}
        />
        <IconWrapper>
          <i className="fa fa-calendar-o" />
        </IconWrapper>
      </InputGroup>

      {error && <ErrorText>{error}</ErrorText>}

      {isOpen && !disabled && (
        <CalendarPopup isRange={isRange}>
          {renderMonth(currentDate)}
          {isRange && renderMonth(currentDate.clone().add(1, 'month'))}
        </CalendarPopup>
      )}
    </Container>
  );
};

SelectDate.propTypes = {
  name: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  error: PropTypes.string,
  isRange: PropTypes.bool,
};

SelectDate.defaultProps = {
  size: 'sm',
  disabled: false,
  value: '',
  onChange: undefined,
  label: undefined,
  error: undefined,
  isRange: false,
};

export default SelectDate;



// WEBPACK FOOTER //
// ./src/design-system/atoms/SelectDate/SelectDate.jsx